import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  Card,
  CardText,
  CardHeader,
  FlatButton,
  Toolbar,
  ToolbarGroup,
} from 'material-ui';
import {
  translate as t,
  showNotification as showNotificationAction,
} from 'admin-on-rest';
import CreateIcon from 'material-ui/svg-icons/content/add';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import EditIcon from 'material-ui/svg-icons/image/edit';
import { iconNameMap } from './IconPicker';
import FolderIcon from './FolderIcon';
import FileIcon from './FileIcon';
import ItemWrapper from './ItemWrapper';

import * as api from './api';
import FolderForm from './FolderForm';
import FileForm from './FileForm';

const positions = [
  'franchisees',
  'areaManager',
  'restaurantManager',
  'socialMediaManager',
  'economy',
  'steward',
  'waiter',
  'barManager',
  'bartender',
  'chef',
  'sousChef',
  'cook',
  'dishwasher',
  'prospect',
];
const ROOT_FOLDER = {
  id: null,
  name: '',
  userPermissions: [],
  rolePermissions: positions,
};
const NO_DIALOG = null;
const FOLDER_FORM_DIALOG = 'folderFormDialog';
const FILE_FORM_DIALOG = 'fileFormDialog';
const INITIAL_FORM = {};
const TRANSLATION_PREFIX = 'documents';

const styles = {
  headerBtn: {
    margin: 10,
  },
};

class Finder extends React.PureComponent {
  state = {
    folderForm: INITIAL_FORM,
    fileForm: INITIAL_FORM,
    currentFolder: ROOT_FOLDER,
    currentDialog: NO_DIALOG,
    content: {
      folders: [],
      files: [],
    },
    users: [],
  }

  componentDidMount() {
    const { currentDirectory } = this.props;
    api.fetchAllUsers()
      .then((users) => {
        this.setState({
          users,
        });
      });
    this.populateDirectory(currentDirectory);
  }

  componentDidUpdate(prevProps) {
    const { currentDirectory } = this.props;
    if (prevProps.currentDirectory !== currentDirectory) {
      this.populateDirectory(currentDirectory);
    }
  }

  setUpdatingFile = (id) => {
    const file = this.state.content.files.find((f) => f.id === id);
    if (file) {
      this.setState({
        fileForm: {
          ...file,
        },
        currentDialog: FILE_FORM_DIALOG,
      });
    }
  }

  updateCurrentFolder = () => {
    if (this.state.currentFolder.id) {
      api.getOneFolder(this.state.currentFolder.id)
        .then((folder) => {
          if (folder) {
            this.setState({
              folderForm: {
                ...folder,
              },
              currentDialog: FOLDER_FORM_DIALOG,
            });
          }
        })
        .catch(this.warn);
    }
  }

  updateContent = (id) => api.getFolderContent(id)
    .then((content) => {
      this.setState({
        content,
      });
    })
    .catch(this.warn);

  updateFolderForm = (delta) => {
    this.setState((prev) => ({
      folderForm: {
        ...prev.folderForm,
        ...delta,
      },
    }));
  }

  updateFileForm = (delta, callback) => {
    this.setState((prev) => ({
      fileForm: {
        ...prev.fileForm,
        ...delta,
      },
    }), callback);
  }

  warn = ({ message }) => this.props.showNotification(message, 'warning')

  deleteCurrentFolder = () => {
    if (this.state.currentFolder.id) {
      api.getOneFolder(this.state.currentFolder.id)
        .then(({ id, parent }) => {
          const promises = [
            parent,
            api.removeFolder(id),
          ];
          return Promise.all(promises);
        })
        .then(([parent]) => {
          if (parent) {
            this.changeDirectory(parent.id);
          } else {
            this.changeDirectory(ROOT_FOLDER.id);
          }
        })
        .catch(this.warn);
    }
  }

  deleteCurrentFile = () => {
    if (this.state.fileForm.id) {
      api.removeFile(this.state.fileForm.id)
        .then(() => this.updateContent(this.state.currentFolder.id))
        .then(this.closeFileForm)
        .catch(this.warn);
    }
  }

  saveFolderForm = () => {
    const isUpdate = this.state.folderForm.id !== null
      && this.state.folderForm.id !== undefined;
    const saveAction = isUpdate ? api.updateFolder : api.createFolder;
    return saveAction(this.state.folderForm)
      .then(() => this.populateDirectory(this.state.currentFolder.id))
      .then(this.closeFolderForm)
      .catch(this.warn);
  }

  saveFileForm = () => {
    const isUpdate = this.state.fileForm.id !== null
      && this.state.fileForm.id !== undefined;
    const saveAction = isUpdate ? api.updateFile : api.createFile;
    return saveAction(this.state.fileForm)
      .then(() => this.updateContent(this.state.currentFolder.id))
      .then(this.closeFileForm)
      .catch(this.warn);
  }

  closeFolderForm = () => {
    this.setState({
      folderForm: INITIAL_FORM,
      currentDialog: NO_DIALOG,
    });
  }

  closeFileForm = () => {
    this.setState({
      fileForm: INITIAL_FORM,
      currentDialog: NO_DIALOG,
    });
  }

  changeDirectory = (id) => {
    const { history } = this.props;
    history.push(`/documents${id ? `/${id}` : ''}`);
  }

  populateDirectory = (id) => {
    if (id) {
      this.setState({
        content: {
          folders: [],
          files: [],
        },
      });
      return api.getOneFolder(id)
        .then(({ id: idFromDb, name, userPermissions, rolePermissions }) => {
          this.updateContent(idFromDb);
          this.setState({
            currentFolder: {
              id: idFromDb,
              name,
              userPermissions,
              rolePermissions,
            },
          });
        })
        .catch(() => this.changeDirectory(ROOT_FOLDER.id));
    }
    // This is the root directory
    this.setState({
      currentFolder: ROOT_FOLDER,
    });
    return this.updateContent();
  }

  newFolder = () => {
    this.setState(({ currentFolder: { id, rolePermissions } }) => ({
      folderForm: {
        name: this.props.translate(`${TRANSLATION_PREFIX}.directory.defaultName`),
        parent: id,
        userPermissions: [],
        rolePermissions,
      },
      currentDialog: FOLDER_FORM_DIALOG,
    }));
  }

  newFile = () => {
    this.setState(({ currentFolder: { id } }) => ({
      fileForm: {
        name: this.props.translate(`${TRANSLATION_PREFIX}.files.defaultName`),
        parent: id,
        files: [],
      },
      currentDialog: FILE_FORM_DIALOG,
    }));
  }

  availableUsers = () => this.state.users;

  availableRoles = () => {
    const { folderForm: { parent, id }, currentFolder: { rolePermissions } } = this.state;
    const isUpdate = !!(id);
    const childOfRoot = !parent;

    if (isUpdate) {
      return childOfRoot ? ROOT_FOLDER.rolePermissions : parent.rolePermissions;
    }

    return childOfRoot ? ROOT_FOLDER.rolePermissions : rolePermissions;
  }

  content = () => {
    const folders = this.state.content.folders.map(({
      id, name, icon,
    }) => (
      <ItemWrapper
        key={id}
        title={name}
        onClick={() => this.changeDirectory(id)}
        icon={(
          <FolderIcon
            size={60}
            icon={iconNameMap[icon]}
          />
        )}
      />
    ));

    const files = this.state.content.files.map(({
      id, name, fileTitle, hasExternalLink,
    }) => (
      <ItemWrapper
        key={id}
        title={name}
        onClick={() => this.setUpdatingFile(id)}
        icon={(
          <FileIcon
            size={45}
            fileName={fileTitle}
            isLink={hasExternalLink}
            style={{
              marginBottom: 10,
            }}
          />
        )}
      />
    ));
    return [
      ...folders,
      ...files,
    ];
  }

  render() {
    const {
      translate,
    } = this.props;
    console.log(this.state.fileForm);
    return (
      <Card style={{ marginBottom: '1em' }}>
        <CardHeader
          title={translate('resources.documents.name')}
          subtitle={this.state.currentFolder.name}
        >
          <Toolbar style={{ backgroundColor: 'white', padding: 0, height: 'unset' }}>
            <ToolbarGroup style={{ flexWrap: 'wrap' }}>
              <FlatButton
                style={styles.headerBtn}
                icon={<CreateIcon />}
                label={translate(`${TRANSLATION_PREFIX}.files.new`)}
                onClick={this.newFile}
              />
              <FlatButton
                style={styles.headerBtn}
                icon={<CreateIcon />}
                label={translate(`${TRANSLATION_PREFIX}.directory.new`)}
                onClick={this.newFolder}
              />
              {
                this.state.currentFolder.id && (
                  <FlatButton
                    style={styles.headerBtn}
                    icon={<DeleteIcon />}
                    label={translate(`${TRANSLATION_PREFIX}.directory.removeCurrent`)}
                    onClick={this.deleteCurrentFolder}
                  />
                )
              }
              {
                this.state.currentFolder.id && (
                  <FlatButton
                    style={styles.headerBtn}
                    icon={<EditIcon />}
                    label={translate(`${TRANSLATION_PREFIX}.directory.updateCurrent`)}
                    onClick={this.updateCurrentFolder}
                  />
                )
              }
            </ToolbarGroup>
          </Toolbar>
        </CardHeader>
        <CardText
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            paddingBottom: 32,
          }}
        >
          {this.content()}
        </CardText>
        <FolderForm
          open={this.state.currentDialog === FOLDER_FORM_DIALOG}
          onChange={this.updateFolderForm}
          onSave={this.saveFolderForm}
          onClose={this.closeFolderForm}
          users={this.availableUsers()}
          roles={this.availableRoles()}
          {...this.state.folderForm}
        />
        <FileForm
          open={this.state.currentDialog === FILE_FORM_DIALOG}
          onChange={this.updateFileForm}
          onSave={this.saveFileForm}
          onClose={this.closeFileForm}
          onRemove={this.state.fileForm.id && this.deleteCurrentFile}
          {...this.state.fileForm}
        />
      </Card>
    );
  }
}

export default connect((state) => {
  const path = state.routing.location.pathname;
  const segments = path.split('/');
  const idIdx = segments.indexOf('documents') + 1;
  const isRoot = idIdx >= segments.length;
  return {
    currentDirectory: isRoot ? null : segments[idIdx],
  };
}, {
  showNotification: showNotificationAction,
})(withRouter(t(Finder)));
